import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, asApiError, type ApiError } from "../api/client";
import type { SessionUser } from "../types";
import { isRecord, stringValue } from "../types";

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  error: ApiError | undefined;
  login: (identifier: string, password: string) => Promise<SessionUser>;
  loginWithOtp: (challengeId: string, code: string) => Promise<SessionUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
  resellerOnly: boolean;
  customerOnly: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

function readStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function normalizeUser(payload: unknown): SessionUser {
  const source = isRecord(payload) && isRecord(payload["user"]) ? payload["user"] : isRecord(payload) ? payload : {};
  const locale = source["locale"] === "en" ? "en" : "ar";
  return {
    id: stringValue(source["id"], stringValue(source["publicId"])),
    publicId: stringValue(source["publicId"], stringValue(source["id"])),
    displayName: stringValue(source["displayName"], stringValue(source["display_name"], stringValue(source["email"], "Aman"))),
    email: stringValue(source["email"]),
    locale,
    type: stringValue(source["type"], "admin"),
    permissions: readStringList(source["permissions"]),
    roles: readStringList(source["roles"]),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError>();
  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await api.get<unknown>("/auth/me");
      setUser(normalizeUser(result));
    } catch (caught) {
      const normalized = asApiError(caught);
      if (["AUTH_SESSION_REQUIRED", "AUTH_SESSION_EXPIRED"].includes(normalized.code) || normalized.status === 401) setUser(null);
      else setError(normalized);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  const login = useCallback(async (identifier: string, password: string): Promise<SessionUser> => {
    await api.post<unknown>("/auth/login", { identifier, password });
    const result = await api.get<unknown>("/auth/me");
    const next = normalizeUser(result);
    setUser(next);
    setError(undefined);
    return next;
  }, []);
  const loginWithOtp = useCallback(async (challengeId: string, code: string): Promise<SessionUser> => {
    await api.post<unknown>("/auth/otp/verify", { challengeId, code });
    const result = await api.get<unknown>("/auth/me");
    const next = normalizeUser(result); setUser(next); setError(undefined); return next;
  }, []);
  const logout = useCallback(async (): Promise<void> => {
    try { await api.post<unknown>("/auth/logout", {}); }
    finally { setUser(null); }
  }, []);
  const value = useMemo<AuthState>(() => {
    const can = (permission: string): boolean => user?.roles.includes("super_admin") === true || user?.permissions.includes("*") === true || user?.permissions.includes(permission) === true;
    const resellerOnly = user?.type === "reseller";
    const customerOnly = user?.type === "customer";
    return { user, loading, error, login, loginWithOtp, logout, refresh, can, resellerOnly, customerOnly };
  }, [error, loading, login, loginWithOtp, logout, refresh, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
